/* Demonstrasjon: one valve list becomes a proposal in five minutes. Centred copy on the dark stage, the system wide
   below it, and the six moments as a timeline that plays itself – a click on a moment takes over for a while.
   The system is the same live components as everywhere else (SkView); the first thing shown is the valve table. */
import { useCallback, useEffect, useRef, useState } from "react";
import { Inert, Stage } from "./Stage";
import { SkView } from "./sk/Shots";
import { useReveal } from "./proposalUtils";

const STEP_MS = 6500;
const MANUAL_MS = 30000;

const Timeline = ({ steps, current, loop, paused, onSelect }) => {
  const refs = useRef([]);
  const onKey = (e, i) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const n = (i + (e.key === "ArrowRight" ? 1 : -1) + steps.length) % steps.length;
    onSelect(n);
    refs.current[n]?.focus();
  };
  return (
    <ol className="kvl-demo-tl" role="tablist" aria-label="Fem minutter" data-paused={paused ? "true" : "false"} data-testid="tilbod-showcase-steps">
      {steps.map((st, i) => {
        const on = i === current;
        return (
          <li key={st.t} className={on ? "is-on" : i < current ? "is-done" : ""} data-testid={`tilbod-showcase-step-${i + 1}`}>
            <button type="button" role="tab" ref={(el) => { refs.current[i] = el; }} aria-selected={on} tabIndex={on ? 0 : -1} onClick={() => onSelect(i)} onKeyDown={(e) => onKey(e, i)} data-testid={`tilbod-showcase-step-btn-${i + 1}`}>
              <span className="kvl-demo-t">{st.t}</span>
              <span className="kvl-demo-track" aria-hidden="true">{on && <i key={`${i}-${loop}`} style={{ "--dur": `${STEP_MS}ms` }} />}</span>
              <span className="kvl-demo-name">{st.title}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
};

export const Demo = ({ d }) => {
  const sc = d.showcase;
  const ref = useReveal();
  const n = sc.steps.length;
  const start = Math.max(0, sc.steps.findIndex((s) => s.first));
  const [active, setActive] = useState(false);
  const [st, setSt] = useState({ v: start, loop: 0 });
  const [paused, setPaused] = useState(false);
  const manualUntil = useRef(0);
  useEffect(() => {
    if (!active || paused) return undefined;
    const t = setTimeout(() => setSt((s) => (manualUntil.current > Date.now() ? { v: s.v, loop: s.loop + 1 } : { v: (s.v + 1) % n, loop: s.loop + 1 })), STEP_MS);
    return () => clearTimeout(t);
  }, [st, active, paused, n]);
  const select = useCallback((i) => {
    manualUntil.current = Date.now() + MANUAL_MS;
    setSt((s) => ({ v: i, loop: s.loop + 1 }));
  }, []);
  const cur = sc.steps[st.v];
  return (
    <section id="showcase" className="kvl-sec kvl-tone-dark kvl-demo" data-head={sc.eyebrow} data-tone="dark" data-testid="tilbod-showcase" ref={ref}>
      <div className="kvl-demo-head kvl-reveal">
        <p className="kvl-demo-eyebrow" data-testid="tilbod-showcase-eyebrow">{sc.eyebrow}</p>
        <h2 className="kvl-h2 kvl-demo-title" data-testid="tilbod-showcase-title">
          {sc.title.split("\n").map((l, i, a) => (
            <span key={l} className="kvl-line">
              {l}
              {i < a.length - 1 && <br />}
            </span>
          ))}
        </h2>
        <p className="kvl-demo-lede" data-testid="tilbod-showcase-lede">{sc.lede}</p>
        {sc.meta && (
          <p className="kvl-demo-meta kvl-mono" data-testid="tilbod-showcase-meta">
            {sc.meta.map((m, i) => (
              <span key={m}>
                {i > 0 && <span className="kvl-demo-meta-dot" aria-hidden="true">·</span>}
                {m}
              </span>
            ))}
          </p>
        )}
      </div>
      <div className="kvl-demo-stage kvl-reveal kvl-reveal-2" data-testid="tilbod-showcase-doc" data-step={st.v} data-view={cur.view}>
        <Timeline steps={sc.steps} current={st.v} loop={st.loop} paused={paused || !active} onSelect={select} />
        <div className="kvl-frame kvl-demo-frame" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          <Stage url={`${d.shots.host}${cur.url}`} caption={d.ui.prototype} height={820} narrowHeight={640} widths={{ fullWidth: 1360 }} testId="tilbod-showcase-mockup" attrs={{ "data-view": cur.view }} onActive={setActive}>
            {({ mode }) => (
              <Inert className="h-full w-full">
                <div key={cur.view} className="mb-view-enter h-full w-full" data-testid={`tilbod-showcase-view-${cur.view}`}>
                  <SkView k={cur.view} mode={mode} ns="sk-demo" />
                </div>
              </Inert>
            )}
          </Stage>
        </div>
        {cur.caption ? (
          <p key={cur.view} className="kvl-demo-cap kvl-cap-enter" data-testid="tilbod-showcase-caption">{cur.caption}</p>
        ) : sc.note ? (
          <p className="kvl-demo-cap" data-testid="tilbod-showcase-caption">{sc.note}</p>
        ) : null}
      </div>
    </section>
  );
};
